import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { Check, Sparkles, ShieldCheck, ArrowRight, Zap, Building2, Handshake } from "lucide-react";

function fmtMxn(n) {
  return "$" + Number(n || 0).toLocaleString("es-MX");
}

export default function Planes() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const { user } = useAuth();
  const { hasAllAccess, subscription, refresh } = useSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/subscription-plans")
      .then((r) => setPlans(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  const startCheckout = async (slug) => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent("/planes")}`);
      return;
    }
    setStarting(slug);
    try {
      const r = await api.post("/subscriptions/checkout", { plan_slug: slug });
      try { sessionStorage.setItem(`dcc_checkout_${r.data.session_id}`, slug); } catch (_) {}
      navigate(r.data.checkout_url);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = detail?.code;
      if (code === "already_subscribed") {
        toast.info("Ya tienes una suscripción activa.");
        refresh();
        navigate("/app/alumno/suscripcion");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setStarting(null);
    }
  };

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Fase 14.6 · Banner B2B/B2B2C */}
        <div className="mb-8 grid sm:grid-cols-2 gap-3" data-testid="planes-b2b-banner">
          <Link
            to="/para-instituciones"
            data-testid="planes-b2b-link"
            className="p-4 rounded-xl border border-slate-200 bg-white hover:border-primary hover:shadow-md transition-all flex items-start gap-3 group"
          >
            <div className="w-10 h-10 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900 group-hover:text-primary">
                ¿Buscas licencias para tu equipo?
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Ver planes enterprise para instituciones →
              </p>
            </div>
          </Link>
          <Link
            to="/para-aliados"
            data-testid="planes-b2b2c-link"
            className="p-4 rounded-xl border border-slate-200 bg-white hover:border-primary hover:shadow-md transition-all flex items-start gap-3 group"
          >
            <div className="w-10 h-10 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
              <Handshake className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900 group-hover:text-primary">
                ¿Quieres ofrecer capacitación bajo tu marca?
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Ver aliados white-label →
              </p>
            </div>
          </Link>
        </div>

        <div className="text-center">
          <span
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-[11px] font-bold uppercase tracking-widest"
            data-testid="mock-badge"
          >
            <Zap className="w-3 h-3" /> Pago mock · sin cargo real
          </span>
          <p className="mt-6 text-xs uppercase tracking-widest text-[#F26A21] font-semibold">
            Individual · Autoservicio
          </p>
          <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0B0B0B] font-display leading-tight">
            All-Access personal para profesionales
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Acceso ilimitado a los 195+ programas mientras tu suscripción esté vigente.
            Cancela cuando quieras. Para uso individual · para equipos, ver{" "}
            <Link to="/para-instituciones" className="text-primary underline hover:brightness-110">planes enterprise</Link>.
          </p>
        </div>

        {hasAllAccess && subscription && (
          <div
            className="mt-8 mx-auto max-w-2xl p-4 rounded-xl border border-emerald-300 bg-emerald-50 flex items-center gap-3"
            data-testid="already-subscribed-banner"
          >
            <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-900">
                Ya tienes {subscription.plan_name} activo
              </p>
              <p className="text-xs text-emerald-800">
                Vigente hasta{" "}
                {new Date(subscription.current_period_end).toLocaleDateString("es-MX", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
            </div>
            <Link
              to="/app/alumno/suscripcion"
              className="text-sm font-semibold text-emerald-800 hover:text-emerald-900 underline underline-offset-2"
              data-testid="go-to-my-sub"
            >
              Gestionar →
            </Link>
          </div>
        )}

        {loading ? (
          <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[0, 1].map((i) => (
              <div key={i} className="h-96 bg-gray-100 border border-gray-200 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div
            className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto"
            data-testid="plans-grid"
          >
            {plans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                highlighted={p.billing_cycle === "annual"}
                onStart={() => startCheckout(p.slug)}
                busy={starting === p.slug}
                disabled={hasAllAccess}
              />
            ))}
          </div>
        )}

        <div className="mt-16 max-w-3xl mx-auto text-center">
          <h2 className="text-xl font-bold text-[#0B0B0B]">Preguntas frecuentes</h2>
          <div className="mt-6 grid gap-4 text-left">
            <Faq
              q="¿Cómo funciona el acceso?"
              a="Mientras tu suscripción esté vigente, puedes iniciar cualquier programa publicado del catálogo (excepto tailor-made). El acceso finaliza cuando cancelas y termina el periodo pagado."
            />
            <Faq
              q="¿Puedo comprar programas individuales aparte?"
              a="Sí. El flujo de compra por programa sigue disponible en el catálogo. All-Access es una alternativa complementaria, no reemplaza las compras individuales."
            />
            <Faq
              q="¿Se renueva automáticamente?"
              a="Sí, salvo que canceles antes del fin del periodo. Al cancelar mantienes el acceso hasta el final del ciclo actual y no se cobrará el siguiente."
            />
            <Faq
              q="¿Qué pasa con los certificados?"
              a="Los certificados IADA y DCC estándar se emiten normalmente cuando cumples los criterios de finalización, sin costo adicional."
            />
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

function PlanCard({ plan, highlighted, onStart, busy, disabled }) {
  const monthly = plan.billing_cycle === "monthly";
  const price = plan.price_mxn;
  const perMonth = monthly ? price : Math.round(price / 12);
  return (
    <div
      data-testid={`plan-card-${plan.slug}`}
      className={`relative p-8 rounded-2xl border-2 flex flex-col ${
        highlighted
          ? "border-[#F26A21] bg-white shadow-lg"
          : "border-gray-200 bg-white"
      }`}
    >
      {highlighted && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F26A21] text-[#0B0B0B] text-[10px] font-bold uppercase tracking-widest">
          <Sparkles className="w-3 h-3" /> Recomendado
        </span>
      )}
      <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
        {monthly ? "Mensual" : "Anual"}
      </p>
      <h3 className="mt-2 text-2xl font-bold text-[#0B0B0B]">{plan.name}</h3>
      <p className="mt-3 text-sm text-gray-600">{plan.description}</p>

      <div className="mt-6">
        <div className="flex items-baseline gap-2">
          <span
            className="text-5xl font-extrabold text-[#0B0B0B] tabular-nums"
            style={{ letterSpacing: "-0.02em" }}
            data-testid={`plan-price-${plan.slug}`}
          >
            {fmtMxn(price)}
          </span>
          <span className="text-sm font-semibold text-gray-600">MXN</span>
        </div>
        <p className="mt-1 text-xs text-gray-500 font-mono">
          {monthly ? "/ mes · facturación mensual" : `/ año · equivale a ${fmtMxn(perMonth)} / mes`}
        </p>
      </div>

      <ul className="mt-6 space-y-3 flex-1">
        {(plan.benefits || []).map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#0B0B0B]">
            <Check className="w-4 h-4 text-[#F26A21] flex-shrink-0 mt-0.5" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onStart}
        disabled={busy || disabled}
        data-testid={`plan-cta-${plan.slug}`}
        className={`mt-8 w-full inline-flex items-center justify-center gap-2 py-3 rounded-md font-semibold text-sm transition-colors ${
          disabled
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : highlighted
              ? "bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B]"
              : "bg-[#0B0B0B] hover:bg-[#1F2937] text-white"
        }`}
      >
        {disabled ? (
          "Ya tienes All-Access"
        ) : busy ? (
          "Iniciando..."
        ) : (
          <>
            Suscribirme (MOCK) <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>
      <p className="mt-3 text-[11px] text-center text-gray-500">
        Sin permanencia · cancela cuando quieras
      </p>
    </div>
  );
}

function Faq({ q, a }) {
  return (
    <div className="p-4 rounded-xl border border-gray-200 bg-white">
      <p className="text-sm font-semibold text-[#0B0B0B]">{q}</p>
      <p className="mt-1 text-sm text-gray-600">{a}</p>
    </div>
  );
}
